import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Circle } from "lucide-react";
import { cn } from "@/lib/utils";

interface GoDecisionItem {
  label: string;
  checked: boolean;
  description?: string;
}

interface GoDecisionBlockProps {
  data?: {
    items?: GoDecisionItem[];
    overall_ready?: boolean;
  };
  recommendedOption?: string;
}

// Default checklist if not provided by AI
const DEFAULT_CHECKLIST: GoDecisionItem[] = [
  { 
    label: "Strategische Passung", 
    checked: true, 
    description: "Option passt zur Gesamtstrategie" 
  },
  { 
    label: "Hypothesenvalidierung möglich", 
    checked: true, 
    description: "30-Tage-Test kann kritische Annahme prüfen" 
  },
  { 
    label: "Risiko kontrollierbar", 
    checked: true, 
    description: "Klare Pivot-Trigger und Exit-Kriterien definiert" 
  },
];

export function GoDecisionBlock({ data, recommendedOption }: GoDecisionBlockProps) {
  const items = data?.items || DEFAULT_CHECKLIST;
  const allChecked = items.every(item => item.checked);
  const overallReady = data?.overall_ready ?? allChecked;

  return (
    <Card 
      id="go-decision"
      className={cn(
        "border-2 scroll-mt-24",
        overallReady 
          ? "border-green-500/50 bg-gradient-to-br from-green-500/10 via-green-500/5 to-transparent" 
          : "border-amber-500/50 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent"
      )}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            {overallReady ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <Circle className="h-5 w-5 text-amber-500" />
            )}
            Go-Decision Check
          </span>
          {overallReady ? (
            <span className="text-sm font-normal text-green-600 dark:text-green-400">
              ✓ Bereit zur Umsetzung
            </span>
          ) : (
            <span className="text-sm font-normal text-amber-600 dark:text-amber-400">
              ⚠ Offene Punkte
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {items.map((item, index) => (
            <div 
              key={index}
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg transition-colors",
                item.checked ? "bg-green-500/10" : "bg-amber-500/10"
              )}
            >
              {item.checked ? (
                <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
              ) : (
                <Circle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              )}
              <div>
                <p className={cn(
                  "font-medium text-sm",
                  item.checked ? "text-green-700 dark:text-green-300" : "text-amber-700 dark:text-amber-300"
                )}>
                  {item.label}
                </p>
                {item.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Final Statement */}
        {recommendedOption && overallReady && (
          <div className="mt-4 pt-4 border-t border-green-500/20">
            <p className="text-sm text-center font-medium">
              <span className="text-green-600 dark:text-green-400">Empfehlung:</span>{" "}
              <strong>{recommendedOption}</strong> umsetzen und in 30 Tagen evaluieren.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
